import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Spinner, EmptyState, usePagination, Pagination } from '../../components/UI';
import { SectionIcon } from '../../components/StudentUI';
import { fmtDate, fmtPct } from '../../utils/format';

const ReadOnlyNote = ({ children }) => (
  <div className="stu-readonly-note">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
    </svg>
    {children}
  </div>
);

export default function StudentAcademicPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/me/academic').then(res => { if (!cancelled) setData(res.data.data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const records = data?.records || [];
  const summary = data?.summary || [];
  const componentGrades = data?.component_grades || [];
  // Weighted by number of assessments per subject, not a flat average of subject averages.
  const totalAssessments = summary.reduce((sum, r) => sum + Number(r.assessments), 0);
  const overallAvg = totalAssessments > 0
    ? (summary.reduce((sum, r) => sum + Number(r.avg_pct) * Number(r.assessments), 0) / totalAssessments).toFixed(1)
    : null;
  const { paged, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(records);

  if (loading) return <div className="stu-page"><Spinner /></div>;

  return (
    <>
      <div className="stu-topbar">
        <div className="stu-topbar-title">My Academic Performance</div>
        <div className="stu-topbar-sub">Assessments recorded by your trainers</div>
      </div>
      <div className="stu-page">
        <ReadOnlyNote>Scores are entered by your trainers after each assessment.</ReadOnlyNote>

        <div className="stu-card" style={{ marginBottom: 20 }}>
          <div className="stu-card-head">
            <h3 className="d-flex align-items-center"><SectionIcon icon="bi-clipboard2-check" color="var(--sms-stat-cyan)" />Module results</h3>
          </div>
          <div className="stu-card-body" style={{ padding: componentGrades.length ? 0 : undefined }}>
            {componentGrades.length === 0 ? (
              <EmptyState icon="bi-clipboard2-check" message="No module results have been released yet" />
            ) : (
              <table className="stu-table">
                <thead>
                  <tr><th>Module</th><th>Intake</th><th>Exam</th><th className="num">Weighted Total</th><th>Result</th></tr>
                </thead>
                <tbody>
                  {componentGrades.map(g => (
                    <tr key={`${g.intake_code}:${g.module_code}:${g.exam_type}`}>
                      <td>{g.module_name || g.module_code}</td>
                      <td>{g.intake_code}</td>
                      <td style={{ textTransform: 'capitalize' }}>{g.exam_type}</td>
                      <td className="num">{fmtPct(g.weighted_total)}</td>
                      <td><span className={`stu-pill ${g.result === 'pass' ? 'ok' : 'fail'}`}>{g.result === 'pass' ? 'Pass' : 'Fail'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="stu-card">
          <div className="stu-card-head">
            <h3 className="d-flex align-items-center"><SectionIcon icon="bi-graph-up" color="var(--sms-stat-violet)" />Assessment history</h3>
            {overallAvg !== null && <span className="stu-pill ok">Average {overallAvg}</span>}
          </div>
          <div className="stu-card-body" style={{ padding: records.length ? 0 : undefined }}>
            {records.length === 0 ? (
              <EmptyState icon="bi-graph-up" message="No assessments recorded yet" />
            ) : (
              <>
                <table className="stu-table">
                  <thead>
                    <tr><th>Subject</th><th>Intake</th><th>Assessment</th><th className="num">Score</th><th className="num">Grade</th><th>Date</th></tr>
                  </thead>
                  <tbody>
                    {paged.map(r => (
                      <tr key={r.id}>
                        <td>{r.subject}</td>
                        <td>{r.intake_code || '—'}</td>
                        <td>{r.assessment}</td>
                        <td className="num">{r.score} / {r.max_score}</td>
                        <td className="num">{r.grade ? <span className="stu-pill ok">{r.grade}</span> : '—'}</td>
                        <td>{fmtDate(r.assessment_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} start={start} />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
